"use client";

import { useEffect, useRef, useState, useCallback } from "react";

import { openaiRealtimeApi } from "@/lib/api/generated/realtime/realtime";

export type Conversation = {
  role: "user" | "assistant";
  text: string;
  timestamp: string;
  isFinal: boolean;
  /** 読み上げを聞き直すためのURL。Gemini音声チャットでのみ入る */
  audioUrl?: string;
  /** 回答に出てきた登録商品。Gemini音声チャットでのみ入る */
  products?: { id: string; name: string; url: string }[];
};

export type FunctionCallEvent = {
  name: string;
  callId: string;
  arguments: Record<string, unknown>;
};

// Default tool definition for OpenAI Realtime API (used when no toolsConfig provided)
const DEFAULT_TOOLS = [
  {
    type: "function",
    name: "show_clinic_departments",
    description:
      "ユーザーが診療科や受診について質問した時に呼び出します。例: 体調が悪い、受診したい、どんな診療科がある、症状がある、相談したい、治療を受けたい、などの発言があった場合。",
    parameters: {
      type: "object",
      properties: {},
      required: [],
    },
  },
];

// Tool configuration type for OpenAI Realtime API
export type ToolConfig = {
  type: string;
  name: string;
  description: string;
  parameters: {
    type: string;
    properties: Record<string, unknown>;
    required: string[];
  };
};

export type VoiceType =
  | "alloy"
  | "ash"
  | "ballad"
  | "coral"
  | "echo"
  | "marin"
  | "sage"
  | "shimmer"
  | "verse";

const useWebRTCAudioSession = (
  voice: VoiceType,
  tenantId: string,
  prompt: string,
  agentId: string,
  toolsConfig?: ToolConfig[],
  onFunctionCall?: (event: FunctionCallEvent) => void
) => {
  const [status, setStatus] = useState("");
  const [isSessionActive, setIsSessionActive] = useState(false);
  const audioIndicatorRef = useRef<HTMLDivElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const [conversation, setConversation] = useState<Conversation[]>([]);
  const [currentVolume, setCurrentVolume] = useState(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const volumeIntervalRef = useRef<number | null>(null);
  const onFunctionCallRef = useRef(onFunctionCall);
  const agentIdRef = useRef(agentId);
  const currentPageUrlRef = useRef<string | undefined>(undefined);
  // Track current response for interruption
  const currentResponseIdRef = useRef<string | null>(null);
  const currentItemIdRef = useRef<string | null>(null);
  const currentAudioContentIndexRef = useRef<number>(0);

  // Keep refs up to date
  useEffect(() => {
    onFunctionCallRef.current = onFunctionCall;
  }, [onFunctionCall]);

  useEffect(() => {
    agentIdRef.current = agentId;
  }, [agentId]);

  // Configure data channel
  const configureDataChannel = useCallback(
    (dataChannel: RTCDataChannel) => {
      // Only update output modalities. Tools are configured server-side during session creation
      // (including search_products if enabled). Sending tools here would overwrite
      // the server-configured tools and remove search_products.
      const sessionUpdate = {
        type: "session.update",
        session: {
          output_modalities: ["audio"],
        },
      };

      dataChannel.send(JSON.stringify(sessionUpdate));
    },
    []
  );

  // Send function call output to OpenAI (with optional cancellation of current response)
  const sendFunctionCallOutput = useCallback(
    (callId: string, output: string, cancelCurrentResponse = true) => {
      if (!dataChannelRef.current) return;

      // Cancel current response if any (interrupts AI speech)
      if (cancelCurrentResponse) {
        const cancelEvent = {
          type: "response.cancel",
        };
        dataChannelRef.current.send(JSON.stringify(cancelEvent));
      }

      const outputEvent = {
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: callId,
          output: output,
        },
      };

      dataChannelRef.current.send(JSON.stringify(outputEvent));

      // Trigger response generation
      const responseEvent = {
        type: "response.create",
      };
      dataChannelRef.current.send(JSON.stringify(responseEvent));
    },
    []
  );

  // Send text message to OpenAI (for user actions like clicking)
  const sendTextMessage = useCallback((message: string) => {
    if (!dataChannelRef.current) return;

    // Add user message to conversation
    const userMessage: Conversation = {
      role: "user",
      text: message,
      timestamp: new Date().toISOString(),
      isFinal: true,
    };
    setConversation((prev) => [...prev, userMessage]);

    // Create a user message
    const messageEvent = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text: message,
          },
        ],
      },
    };

    dataChannelRef.current.send(JSON.stringify(messageEvent));

    // Trigger response generation
    const responseEvent = {
      type: "response.create",
    };
    dataChannelRef.current.send(JSON.stringify(responseEvent));
  }, []);

  // Switch between text-only and audio output modalities
  const setTextOnlyMode = useCallback((enabled: boolean) => {
    if (!dataChannelRef.current) return;

    // Only update output modalities, not tools (tools are server-configured)
    const sessionUpdate = {
      type: "session.update",
      session: {
        output_modalities: enabled ? ["text"] : ["audio"],
      },
    };
    dataChannelRef.current.send(JSON.stringify(sessionUpdate));

    // Mute/unmute microphone
    if (audioStreamRef.current) {
      audioStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !enabled;
      });
    }

    // Clear input audio buffer when switching to text mode
    if (enabled) {
      const clearAudioEvent = {
        type: "input_audio_buffer.clear",
      };
      dataChannelRef.current.send(JSON.stringify(clearAudioEvent));
    }
  }, []);

  // Cancel current response and send new message (interrupts AI speech)
  const interruptAndSendMessage = useCallback((message: string) => {
    if (!dataChannelRef.current) return;

    // Cancel current response if any
    const cancelEvent = {
      type: "response.cancel",
    };
    dataChannelRef.current.send(JSON.stringify(cancelEvent));

    // Truncate the current audio item to stop playback immediately
    if (currentItemIdRef.current) {
      const truncateEvent = {
        type: "conversation.item.truncate",
        item_id: currentItemIdRef.current,
        content_index: currentAudioContentIndexRef.current,
        audio_end_ms: 0, // Truncate at current position
      };
      dataChannelRef.current.send(JSON.stringify(truncateEvent));
    }

    // Clear input audio buffer to prevent any pending audio from being processed
    const clearAudioEvent = {
      type: "input_audio_buffer.clear",
    };
    dataChannelRef.current.send(JSON.stringify(clearAudioEvent));

    // Clear tracking refs
    currentResponseIdRef.current = null;
    currentItemIdRef.current = null;
    currentAudioContentIndexRef.current = 0;

    // Create a user message
    const messageEvent = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text: message,
          },
        ],
      },
    };

    dataChannelRef.current.send(JSON.stringify(messageEvent));

    // Trigger new response generation
    const responseEvent = {
      type: "response.create",
    };
    dataChannelRef.current.send(JSON.stringify(responseEvent));
  }, []);

  // Handle data channel messages
  const handleDataChannelMessage = useCallback(async (event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data);

      // Track response creation for interruption
      if (msg.type === "response.created") {
        currentResponseIdRef.current = msg.response?.id || null;
        currentItemIdRef.current = null;
        currentAudioContentIndexRef.current = 0;
      }

      // Track output item for truncation
      if (msg.type === "response.output_item.added") {
        currentItemIdRef.current = msg.item?.id || null;
      }

      // Track audio content index
      if (msg.type === "response.audio.delta") {
        currentAudioContentIndexRef.current = msg.content_index ?? 0;
      }

      // Clear tracking when response is done
      if (msg.type === "response.done" || msg.type === "response.cancelled") {
        currentResponseIdRef.current = null;
        currentItemIdRef.current = null;
        currentAudioContentIndexRef.current = 0;
      }

      // AI transcription (voice mode)
      if (msg.type === "response.audio_transcript.done") {
        const assistantMessage: Conversation = {
          role: "assistant",
          text: msg.transcript,
          timestamp: new Date().toISOString(),
          isFinal: false,
        };
        setConversation((prev) => [...prev, assistantMessage]);
      }

      // AI text response (text-only mode)
      if (msg.type === "response.text.done") {
        const assistantMessage: Conversation = {
          role: "assistant",
          text: msg.text,
          timestamp: new Date().toISOString(),
          isFinal: true,
        };
        setConversation((prev) => [...prev, assistantMessage]);
      }

      // User transcription
      if (msg.type === "conversation.item.input_audio_transcription.completed") {
        const userMessage: Conversation = {
          role: "user",
          text: msg.transcript || "",
          timestamp: new Date().toISOString(),
          isFinal: true,
        };
        setConversation((prev) => [...prev, userMessage]);
      }

      // Function call
      if (msg.type === "response.function_call_arguments.done") {
        const functionCallEvent: FunctionCallEvent = {
          name: msg.name,
          callId: msg.call_id,
          arguments: msg.arguments ? JSON.parse(msg.arguments) : {},
        };
        onFunctionCallRef.current?.(functionCallEvent);
      }

      return msg;
    } catch (error) {
      console.error("Error handling data channel message:", error);
    }
  }, []);

  useEffect(() => {
    return () => stopSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEphemeralToken = useCallback(
    async (voice: VoiceType) => {
      const data = await openaiRealtimeApi(
        tenantId,
        {
          introduction: prompt,
        },
        {
          voice,
          agentId: agentIdRef.current,
          currentPageUrl: currentPageUrlRef.current,
        }
      );
      return data.ephemeralKey || "";
    },
    [tenantId, prompt]
  );

  const setupAudioVisualization = useCallback((stream: MediaStream) => {
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyzer = audioContext.createAnalyser();
    analyzer.fftSize = 256;

    source.connect(analyzer);

    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateIndicator = () => {
      if (!audioContext) return;

      analyzer.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / bufferLength;

      if (audioIndicatorRef.current) {
        audioIndicatorRef.current.classList.toggle("active", average > 30);
      }

      requestAnimationFrame(updateIndicator);
    };

    updateIndicator();
    audioContextRef.current = audioContext;
  }, []);

  const getVolume = useCallback((): number => {
    if (!analyserRef.current) return 0;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);

    // Calculate RMS (Root Mean Square)
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const float = (dataArray[i] - 128) / 128;
      sum += float * float;
    }

    return Math.sqrt(sum / dataArray.length);
  }, []);

  const startSession = useCallback(async (currentPageUrl?: string) => {
    try {
      currentPageUrlRef.current = currentPageUrl;
      setStatus("Requesting microphone access...");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      setupAudioVisualization(stream);

      setStatus("Fetching ephemeral token...");
      const ephemeralToken = await getEphemeralToken(voice);
      if (!ephemeralToken) {
        throw new Error("OpenAI Realtime client secret was empty");
      }

      setStatus("Establishing connection...");

      const pc = new RTCPeerConnection();
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;

      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];

        // Set up audio analysis
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(e.streams[0]);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        source.connect(analyser);
        analyserRef.current = analyser;

        // Start volume monitoring
        volumeIntervalRef.current = window.setInterval(() => {
          const volume = getVolume();
          setCurrentVolume(volume);
        }, 100);
      };

      // Add data channel
      const dataChannel = pc.createDataChannel("oai-events");
      dataChannelRef.current = dataChannel;

      dataChannel.onopen = () => {
        configureDataChannel(dataChannel);
      };

      dataChannel.onmessage = handleDataChannelMessage;

      pc.addTrack(stream.getTracks()[0]);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const response = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralToken}`,
          "Content-Type": "application/sdp",
        },
      });

      const answerSdp = await response.text();
      if (!response.ok) {
        throw new Error(`OpenAI Realtime connection failed (${response.status}): ${answerSdp}`);
      }

      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });

      peerConnectionRef.current = pc;
      setIsSessionActive(true);
      setStatus("Session established successfully!");
    } catch (err) {
      console.error(err);
      setStatus(`Error: ${err}`);
      stopSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    voice,
    getEphemeralToken,
    setupAudioVisualization,
    getVolume,
    configureDataChannel,
    handleDataChannelMessage,
  ]);

  const stopSession = useCallback(() => {
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (audioIndicatorRef.current) {
      audioIndicatorRef.current.classList.remove("active");
    }

    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current = null;
    }

    setCurrentVolume(0);
    setIsSessionActive(false);
    setStatus("");
  }, []);

  return {
    status,
    isSessionActive,
    audioIndicatorRef,
    startSession,
    stopSession,
    currentVolume,
    conversation,
    setConversation,
    sendFunctionCallOutput,
    sendTextMessage,
    interruptAndSendMessage,
    setTextOnlyMode,
  };
};

export default useWebRTCAudioSession;
